import React, { Fragment, useState, useEffect, useMemo, useCallback } from 'react';
import { observer, useLocalObservable } from 'mobx-react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import ErrorMessage from '../../components/errormessage';
import { formatDate } from '../../utils/helpers';
import PackageTable from '../../components/super/packages';
import AddPackage from '../../components/super/add-package';
import AddManager from '../../components/super/add-manager';
import ContentGroupAccess from '../../components/super/content-group-access';
import Clipboard from 'react-clipboard.js';
import moment from 'moment/moment';
import { enforceSlash } from '../../utils/helpers';
import ThemedSpinner from '../../components/themed/ThemedSpinner';
import Countdown from 'react-countdown';
import SettingsTable from '../../components/super/settings';
import AddSetting from '../../components/super/add-setting';
import SendTestEmail from '../../components/super/send-test-email';
import CompanyTranslationsEditor from '../../components/super/CompanyTranslationsEditor';
import { useStores } from '../../hooks/use-stores';
import SimpleTable from '../../components/super/SimpleTable';
import BillingContactForm from '../../components/super/BillingContactForm';
import BillingDialog from '../../components/super/BillingDialog';
import BillingForm from '../../forms/billing-contact';
//import SetExternalIdModal from '../../components/super/set-external-id-modal';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Box from '@material-ui/core/Box';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import EditIcon from '@material-ui/icons/Edit';
//import SettingsIcon from '@material-ui/icons/Settings';
import ReplayIcon from '@material-ui/icons/Replay';
import { TextField, Switch, FormControlLabel } from '@material-ui/core';
import { IconButton } from '@material-ui/core';
import { Link } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import DeleteIcon from '@material-ui/icons/Delete';
import PersonIcon from '@material-ui/icons/Person';
import SendIcon from '@material-ui/icons/Send';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import TimerIcon from '@material-ui/icons/Timer';
import CircularProgress from '@material-ui/core/CircularProgress';
import { FEATURE_SECTIONS, FEATURE_SETTING_NAMES, getFeatureSettingNames } from '../../constants/featureSettings';

function CompanyView(factory, deps) {
    const TOKEN_TIMER = 1 * 60 * 1000;
    let { companyId } = useParams();
    const { superStore, commonStore, authStore } = useStores();
    const [views, setViews] = useState(100);
    const [notes, setNotes] = useState('');
    // Local dialog state to avoid MobX store interference with React elements
    const [showBillingDialog, setShowBillingDialog] = useState(false);
    const [billingDialogLoading, setBillingDialogLoading] = useState(false);
    const [pendingPackage, setPendingPackage] = useState(null);

    // Track original CB values to detect changes
    const [originalCBEmail, setOriginalCBEmail] = useState('');
    const [originalCBFirstName, setOriginalCBFirstName] = useState('');
    const [originalCBLastName, setOriginalCBLastName] = useState('');

    // Initialize billing form with MobX
    const localStore = useLocalObservable(() => {
        const billingForm = new BillingForm({}, {
            options: {
                validateOnChange: true,
                validateOnBlur: true,
                showErrorsOnChange: true,
                showErrorsOnBlur: true,
            },
        });

        return { billingForm };
    });
    //const [showingExternalIdModal, setShowingExternalIdModal] = useState(false);
    const {
        loadingCompany,
        creatingAclLine,
        removingAclLines,
        addingPackage,
        addingCompanyManager,
        addingCompanySetting,
        removingPackages,
        updatingPackages,
        removingCompanyManagers,
        removingCompanySettings,
        loadingAclRulesForPrincipal,
        savingPPV,
        savingPPVApiKey,
        freemiums,
        aclGroups,
        aclRulesForPrincipal,
        managerTokensIndex,
        sendingTestEmail,
        //savingCompany,
        editingCompany: company,
        error,
        subscriptions,
        addPackageError,
        systemSettingsLoading,
    } = superStore;
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const baseUrl = enforceSlash(pathname);

    const [savingFeatureIds, setSavingFeatureIds] = useState(() => new Set());
    const [hideFeaturesInTable, setHideFeaturesInTable] = useState(false);
    const [editPackageId, setEditPackageId] = useState(null);

    const isCustomBillingForPackages = (company?.settings || []).some(
        (s) => s.name === 'billing.custom' && s.value === '1'
    );
    const getCanEditForPackage = useCallback(
        (pkg) => {
            if (!pkg || company?.partnerId !== 1) return false;
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const validToDate = pkg.valid_to ? new Date(pkg.valid_to) : null;
            const isExpired = validToDate && validToDate < today;
            const isDemoPackage =
                pkg.package_name &&
                (pkg.package_name.toLowerCase().startsWith('demo-plan') ||
                    pkg.package_name.toLowerCase().includes('demo'));
            return (
                (isCustomBillingForPackages || isDemoPackage) &&
                pkg.valid &&
                !isExpired
            );
        },
        [company?.partnerId, company?.settings, isCustomBillingForPackages]
    );
    const editingPackage = useMemo(
        () =>
            company?.packages?.find((p) => p.package_id === editPackageId) ?? null,
        [company?.packages, editPackageId]
    );
    const editingPackageCanEdit = editingPackage
        ? getCanEditForPackage(editingPackage)
        : false;
    const hasValidPackage = company?.packages?.some((p) => p.valid === 1);
    const showAddOrEditForm =
        (editPackageId !== null && editingPackageCanEdit) ||
        (editPackageId === null && !hasValidPackage);

    useEffect(() => {
        if (
            editPackageId !== null &&
            (!editingPackage || !editingPackageCanEdit)
        ) {
            setEditPackageId(null);
        }
    }, [editPackageId, editingPackage, editingPackageCanEdit]);

    const filterFeatureKeys = useMemo(
        () => new Set(getFeatureSettingNames()),
        []
    );

    const companySettings = company?.settings || [];
    const systemSettings = superStore.systemSettings || [];
    const systemSettingsMap = superStore.systemSettingsMap;
    const partnerSettings =
        company?.partnerId &&
        superStore.editingPartner &&
        superStore.editingPartner.id === company.partnerId
            ? superStore.editingPartner.settings || []
            : [];
    const filteredCompanySettings = companySettings.filter(
        (setting) =>
            setting &&
            typeof setting.name === 'string' &&
            !setting.name.startsWith('ui.translation.') &&
            (!hideFeaturesInTable || !filterFeatureKeys.has(setting.name))
    );

    const companySettingsMap = useMemo(
        () => new Map(companySettings.map((setting) => [setting.name, setting])),
        [companySettings]
    );

    const partnerSettingsMap = useMemo(
        () => new Map(partnerSettings.map((setting) => [setting.name, setting])),
        [partnerSettings]
    );

    const computeFeatureState = (feature) => {
        if (feature.relatedKeys.some((key) => companySettingsMap.has(key))) {
            return {
                checked: feature.resolve(companySettings),
                source: 'company',
            };
        }

        if (feature.relatedKeys.some((key) => partnerSettingsMap.has(key))) {
            return {
                checked: feature.resolve(partnerSettings),
                source: 'partner',
            };
        }

        if (feature.relatedKeys.some((key) => systemSettingsMap.has(key))) {
            return {
                checked: feature.resolve(systemSettings),
                source: 'system',
            };
        }

        return {
            checked: feature.resolve([]),
            source: null,
        };
    };

    const companyIdForRequests = useMemo(() => {
        const numericId = Number(companyId);
        return Number.isNaN(numericId) ? companyId : numericId;
    }, [companyId]);

    const loadData = async () => {
        await superStore.listFreemiums();
        await superStore.listSubscriptions(companyId);
        await superStore.getSystemSettings();
        await superStore.loadCompany(companyId);
        const currentCompany = superStore.editingCompany;
        if (currentCompany && currentCompany.partnerId) {
            await superStore.loadPartner(currentCompany.partnerId);
        }
        await superStore.loadAclGroups();
        await superStore.loadLanguages();
    };

    const loadAclRulesForCompany = async () => {
        if (company && company.company_uid) {
            await superStore.loadAclRulesForPrincipal(company.company_uid);
        }
    };

    const deleteAclRule = async (id) => {
        await superStore.removeAclLine(id);
        await loadAclRulesForCompany();
    };

    const createAclRule = async (principal_unique_id, unique_id, rule = 1) => {
        await superStore.createAclLine({
            rule,
            principal_unique_id,
            object_unique_id: unique_id,
        });
        await loadAclRulesForCompany();
    };

    const handleFeatureToggle = useCallback(
        async (feature, nextChecked) => {
            const mutations = feature.mutations[nextChecked ? 'true' : 'false'] || [];
            if (!mutations.length) return;

            setSavingFeatureIds((prev) => {
                const next = new Set(prev);
                next.add(feature.id);
                return next;
            });

            try {
                for (const mutation of mutations) {
                    if (mutation.type === 'upsert') {
                        const existing = (company?.settings || []).find(
                            (setting) => setting.name === mutation.key
                        );

                        if (existing) {
                            if (existing.value === mutation.value) continue;
                            superStore.updateCompanySetting(existing.id, mutation.value);
                            await superStore.saveCompanySetting(existing.id, companyIdForRequests);
                        } else {
                            await superStore.addCompanySetting(companyIdForRequests, {
                                name: mutation.key,
                                value: mutation.value,
                            });
                        }
                    } else if (mutation.type === 'delete') {
                        const existing = (company?.settings || []).find(
                            (setting) => setting.name === mutation.key
                        );
                        if (existing) {
                            await superStore.removeCompanySetting(
                                existing.id,
                                companyIdForRequests
                            );
                        }
                    }
                }
            } catch (error) {
                console.error(error);
                commonStore.error(`Failed to update ${feature.label}. Please try again.`);
                await superStore.loadCompany(companyIdForRequests);
            } finally {
                setSavingFeatureIds((prev) => {
                    const next = new Set(prev);
                    next.delete(feature.id);
                    return next;
                });
            }
        },
        [company, superStore, companyIdForRequests, commonStore]
    );

    useEffect(() => {
        loadData();
    }, [companyId]);

    useEffect(() => {
        loadAclRulesForCompany();
    }, [company]);

    // Show addPackageError through useEffect when it changes
    useEffect(() => {
        if (addPackageError) {
            commonStore.error(addPackageError);
        }
    }, [addPackageError]);

    /*const updateExternalId = async (externalId) => {
        await superStore.update(company.company_id, { externalId });
        setShowingExternalIdModal(false);
    };*/

    const sendTestEmail = async (email, type) => {
        const data = { email, type };
        if (
            (await superStore.sendCompanyTestEmail(companyId, data)) &&
            !sendingTestEmail
        )
            commonStore.success('Email sent!');
    };

    const removeSetting = (settingId) => {
        superStore.removeCompanySetting(settingId, companyId);
    };

    const addSetting = (name, value) => {
        const data = {
            name,
            value,
        };
        superStore.addCompanySetting(companyId, data);
    };

    const onChangeSetting = (id, value) => {
        superStore.updateCompanySetting(id, value);
        superStore.debouncedSaveCompanySetting(id, companyId);
    };

    const onBlurSettingValue = (id, value) => {
        superStore.trimCompanySetting(id, value);
    };

    const handleCustomBillingToggle = async (event) => {
        const isEnabled = event.target.checked;
        const setting = (company?.settings || []).find((s) => s.name === 'billing.custom');

        try {
            if (isEnabled) {
                // Enable custom billing
                if (setting) {
                    // Update existing setting to '1'
                    superStore.updateCompanySetting(setting.id, '1');
                    await superStore.saveCompanySetting(setting.id, Number(companyId));
                } else {
                    // Create new setting
                    await superStore.addCompanySetting(Number(companyId), { name: 'billing.custom', value: '1' });
                }
            } else {
                // Disable custom billing
                if (setting) {
                    // Delete the setting (better than setting to '0')
                    await superStore.removeCompanySetting(setting.id, Number(companyId));
                }
            }
            // Settings are already updated in the store, no need to reload entire company
        } catch (error) {
            console.error('Error toggling custom billing:', error);
            commonStore.error('Failed to update custom billing setting');
        }
    };

    const handleAddPackage = async (
        packageUsers,
        valid_from,
        valid_to,
        packageTrial,
        freemiumCode,
        subscription
    ) => {
        try {
            setPendingPackage({ packageUsers, valid_from, valid_to, packageTrial, freemiumCode, subscription });
            setShowBillingDialog(true);
            setBillingDialogLoading(true);

            // Load Chargebee customer info to show in the dialog
            await superStore.loadCompanyChargebeeCustomer(Number(companyId));
            await superStore.loadCompanySubscriptionEstimate(
                subscription,
                packageUsers,
                Number(companyId)
            );

            const cbCustomer = superStore.companyChargebeeCustomer;
            setBillingDialogLoading(false);

            // Reset form to clear any previous validation state
            localStore.billingForm.reset();

            // Prefill form from existing CB customer if exists
            if (cbCustomer) {
                const email = cbCustomer.email || '';
                const firstName = cbCustomer.first_name || '';
                const lastName = cbCustomer.last_name || '';

                // Set to "chargebee_initial" by default if customer exists
                localStore.billingForm.$('contactMode').set('chargebee_initial');

                localStore.billingForm.$('email').set(email);
                localStore.billingForm.$('firstName').set(firstName);
                localStore.billingForm.$('lastName').set(lastName);

                // Store original values for change detection
                setOriginalCBEmail(email);
                setOriginalCBFirstName(firstName);
                setOriginalCBLastName(lastName);
            } else {
                // Set to "custom" by default if no customer
                localStore.billingForm.$('contactMode').set('custom');

                // Clear all form fields if no customer
                localStore.billingForm.$('email').set('');
                localStore.billingForm.$('firstName').set('');
                localStore.billingForm.$('lastName').set('');
                setOriginalCBEmail('');
                setOriginalCBFirstName('');
                setOriginalCBLastName('');

            }

            await localStore.billingForm.validate({ showErrors: true });

            // Data is loaded, dialog will render with reactive inputs
        } catch (err) {
            setBillingDialogLoading(false);
            setShowBillingDialog(false);
            commonStore.error('Error loading estimate.');
            console.error(err);
        }
    };

    const handleBillingContactChange = (val, managers) => {
        if (val !== 'custom' && val !== 'chargebee_initial') {
            const mgr = managers.find((m) => m.email === val);
            if (mgr) {
                localStore.billingForm.$('email').set(mgr.email);
                const parts = (mgr.name || '').split(' ');
                localStore.billingForm.$('firstName').set(parts.slice(0, -1).join(' ') || '');
                localStore.billingForm.$('lastName').set(parts.slice(-1).join(' ') || '');
            }
        }
    };

    const handleRestoreChargebeeData = () => {
        // Restore all original Chargebee values
        localStore.billingForm.$('email').set(originalCBEmail);
        localStore.billingForm.$('firstName').set(originalCBFirstName);
        localStore.billingForm.$('lastName').set(originalCBLastName);
    };

    const handleCustomBilling = async () => {
        const { packageUsers, valid_from, valid_to, packageTrial, freemiumCode, subscription } = pendingPackage;

        // Check if custom billing is already enabled
        const isCurrentlyCustomBilling = (company?.settings || []).some(s => s.name === 'billing.custom' && s.value === '1');

        if (!isCurrentlyCustomBilling) {
            // 1) Set company setting billing.custom=1 (only if not already set)
            const setting = (superStore.editingCompany.settings || []).find((s) => s.name === 'billing.custom');
            if (setting) {
                superStore.updateCompanySetting(setting.id, '1');
                await superStore.saveCompanySetting(setting.id, Number(companyId));
            } else {
                await superStore.addCompanySetting(Number(companyId), { name: 'billing.custom', value: '1' });
            }
        }

        // 2) Build billing payload with current form values (backend will force sales@awarego.com email)
        const form = localStore.billingForm;
        const formValues = form.values();
        const cbCustomer = superStore.companyChargebeeCustomer;
        const cbExists = !!cbCustomer;

        // Always send billing data for custom billing
        const billingPayload = {
            // Email will be overridden by backend to sales@awarego.com
            billingEmail: 'sales@awarego.com' // Explicitly set for clarity
        };

        if (cbExists) {
            // Only send changed fields
            if (formValues.firstName !== originalCBFirstName) {
                billingPayload.firstName = formValues.firstName;
            }
            if (formValues.lastName !== originalCBLastName) {
                billingPayload.lastName = formValues.lastName;
            }
        } else {
            // New customer: send all fields except email (backend will force email)
            billingPayload.firstName = formValues.firstName;
            billingPayload.lastName = formValues.lastName;
        }

        setShowBillingDialog(false);
        await addPackage(packageUsers, valid_from, valid_to, packageTrial, freemiumCode, subscription, billingPayload);
        setEditPackageId(null);
    };

    const handleNormalBilling = async () => {
        const { packageUsers, valid_from, valid_to, packageTrial, freemiumCode, subscription } = pendingPackage;

        // Validate form
        const form = localStore.billingForm;
        const contactMode = form.$('contactMode').value;

        // If chargebee_initial is selected, don't send any billing data (no changes)
        if (contactMode === 'chargebee_initial') {
            setShowBillingDialog(false);
            await addPackage(packageUsers, valid_from, valid_to, packageTrial, freemiumCode, subscription, null);
            setEditPackageId(null);
            return;
        }

        const formValues = form.values();
        const cbCustomer = superStore.companyChargebeeCustomer;
        const cbExists = !!cbCustomer;

        // Build billing payload: only include changed fields if customer exists
        const billingPayload = { billingEmail: formValues.email };

        if (cbExists) {
            // Only send changed fields
            if (formValues.firstName !== originalCBFirstName) {
                billingPayload.firstName = formValues.firstName;
            }
            if (formValues.lastName !== originalCBLastName) {
                billingPayload.lastName = formValues.lastName;
            }
        } else {
            // New customer: send all fields
            billingPayload.firstName = formValues.firstName;
            billingPayload.lastName = formValues.lastName;
        }

        setShowBillingDialog(false);
        await addPackage(packageUsers, valid_from, valid_to, packageTrial, freemiumCode, subscription, billingPayload);
        setEditPackageId(null);
    };

    const addPackage = async (
        packageUsers,
        valid_from,
        valid_to,
        packageTrial,
        freemiumCode,
        subscription,
        billing
    ) => {
        const data = {};
        data.users = packageUsers;
        data.validFrom = valid_from;
        data.validTo = valid_to;
        data.trial = packageTrial;
        data.company_id = companyId;
        data.freemiumCode = freemiumCode;
        data.subscription = subscription;
        if (billing) {
            data.billing = billing;
        }
        if (editPackageId != null) {
            data.editPackageId = editPackageId;
        }

        await superStore.addPackage(companyId, data);
    };

    const removePackage = async (packageId) => {
        await superStore.removePackage(packageId, companyId);
    };

    /*const onChangePackageType = (packageId, type) => {
        superStore.updatePackage(companyId, packageId, { full_package: type });
    };*/

    const addManager = (name, email, role, sendWelcome) => {
        superStore.addManager(companyId, { name, email, role, sendWelcome });
    };

    const removeManager = (email) => {
        superStore.removeCompanyManager(email, companyId);
    };

    const createPPV = () => {
        superStore.createPPV(companyId);
    };

    const newPPVApiKey = () => {
        superStore.newPPVApiKey(companyId);
    };

    const impersonate = (manager) => {
        if (manager.timer) {
            clearTimeout(manager.timer);
            manager.timer = null;
        }
        let link = superStore.impersonate(manager.email, companyId);
        if (link) {
            manager.timer = setTimeout(() => {
                manager.timer = null;
                superStore.removeToken(manager.email);
            }, TOKEN_TIMER);
        }
    };

    const sendLoginInfo = async (manager) => {
        commonStore.showConfirm(
            `Do you want to send an email to ${manager.email} with a login link for the LMS?`,
            'Send email',
            'Send login email',
            async () => {
                const result = await authStore.signin(
                    manager.email,
                    companyId,
                    null,
                    null,
                    '/',
                    'login-info'
                );
                if (result) commonStore.success('Login email sent!');
            }
        );
    };

    const deleteCompany = async () => {
        commonStore.showDeleteWithInputConfirm(
            <p>
                Are you sure you want to delete the company{' '}
                <strong>{company.company_name}</strong>?<br />
                This action will delete:
                <br />
                *All training programs
                <br />
                *All assessments
                <br />
                *All employee lists
                <br />
                *All employee progress
                <br />
                Along with any other data related to this company
            </p>,
            'Delete company and all data',
            'Delete company',
            'delete',
            async () => {
                const result = await superStore.deleteCompany(
                    company.partnerId || company.accountId || null,
                    companyId
                );
                if (result) {
                    navigate('/companies');
                    commonStore.success('Company was deleted');
                }
            }
        );
    };

    const addViews = () => {
        superStore.addViews(companyId, Number(views) || 0, notes);
    };

    const removeViews = () => {
        superStore.addViews(companyId, -1 * (Number(views) || 0), notes);
    };

    const columns = useMemo(
        () => [
            {
                Header: 'Created',
                accessor: 'created',
                Cell: (x) => formatDate(x.value),
            },
            {
                Header: 'Email',
                accessor: 'email',
            },
            {
                id: 'send',
                accessor: (x) => (
                    <Grid
                        container
                        direction="row"
                        alignItems="center"
                        spacing={1}
                    >
                        <Grid item>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => {
                                    sendLoginInfo(x);
                                }}
                                startIcon={<SendIcon />}
                            >
                                Send login info
                            </Button>
                        </Grid>
                    </Grid>
                ),
            },
            {
                Header: 'Name',
                accessor: 'name',
            },
            {
                Header: 'Role',
                accessor: 'role_name',
            },
            {
                id: 'impersonate',
                accessor: (x) => (
                    <Grid
                        container
                        direction="row"
                        alignItems="center"
                        spacing={1}
                    >
                        <Grid item>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => {
                                    impersonate(x);
                                }}
                                startIcon={<PersonIcon />}
                            >
                                Impersonate
                            </Button>
                        </Grid>
                        {managerTokensIndex[x.email] &&
                            managerTokensIndex[x.email].access_link && (
                                <Fragment>
                                    <Grid item>
                                        <Clipboard
                                            className="copy-to-clipboard-link"
                                            data-clipboard-text={
                                                managerTokensIndex[x.email]
                                                    .access_link
                                            }
                                        >
                                            <Button
                                                variant="contained"
                                                startIcon={<FileCopyIcon />}
                                            >
                                                Copy sign in link
                                            </Button>
                                        </Clipboard>
                                    </Grid>
                                    <Grid item>
                                        <TimerIcon color="primary" />
                                    </Grid>
                                    <Grid item>
                                        <Countdown
                                            date={Date.now() + TOKEN_TIMER}
                                            renderer={(props) => (
                                                <Fragment>
                                                    {props.minutes
                                                        .toString()
                                                        .padStart(2, '0') +
                                                        ':' +
                                                        props.seconds
                                                            .toString()
                                                            .padStart(2, '0')}
                                                </Fragment>
                                            )}
                                        />
                                    </Grid>
                                </Fragment>
                            )}
                    </Grid>
                ),
            },
            {
                id: 'deleteManager',
                accessor: 'email',
                Cell: observer((x) => {
                    return removingCompanyManagers.includes(x.value) ? (
                        <CircularProgress />
                    ) : (
                        <IconButton
                            variant="outlined"
                            color="secondary"
                            onClick={() => {
                                removeManager(x.value);
                            }}
                        >
                            <DeleteIcon fontSize="large" />
                        </IconButton>
                    );
                }),
            },
        ],
        [managerTokensIndex]
    );

    return loadingCompany && !company ? (
        <ThemedSpinner />
    ) : (
        <Grid
            container
            direction="row"
            justify="center"
            alignItems="center"
            align="center"
            spacing={2}
        >
            {company && (
                <Fragment>
                    {error && (
                        <Grid item xs={12}>
                            <ErrorMessage error={error} errorType="error" />
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <Box mt={4}>
                            <Typography variant="h2" gutterBottom>
                                Company{company && `: ${company.company_name}`}
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => navigate(`${baseUrl}edit`)}
                            startIcon={<EditIcon />}
                        >
                            Edit
                        </Button>
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <Button
                            variant="contained"
                            color="default"
                            onClick={() =>
                                navigate(
                                    `${
                                        import.meta.env.REACT_APP_LMS_URL
                                    }/super/companies/${companyId}/automations`
                                )
                            }
                        >
                            View Automations
                        </Button>
                    </Grid>

                    <Grid item xs={12}>
                        <Typography variant="h4" paragraph>
                            Partner
                        </Typography>
                    </Grid>
                    <Grid item xs={6} md={2}>
                        {company.partnerId &&
                            `${company.partnerName} [${company.partnerId}]`}
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() =>
                                company.partnerId &&
                                navigate(`/partners/${company.partnerId}`)
                            }
                        >
                            View partner
                        </Button>
                    </Grid>

                    {/*<Grid item xs={12}>
                        <Typography variant="h4" gutterBottom>
                            External ID
                        </Typography>
                    </Grid>
                    <Grid item xs={6} md={2}>
                        {company.external_id
                            ? company.external_id
                            : 'External ID not yet set'}
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <Button
                            variant="contained"
                            color="default"
                            startIcon={<SettingsIcon />}
                            onClick={() => setShowingExternalIdModal(true)}
                        >
                            Set external ID
                        </Button>
                    </Grid>

                    <SetExternalIdModal
                        onClose={() => setShowingExternalIdModal(false)}
                        companyName={company.company_name}
                        externalId={company.external_id}
                        updateExternalId={updateExternalId}
                        showLoader={savingCompany}
                        open={showingExternalIdModal}
                        />*/}

                    <Grid item xs={12}>
                        <Typography variant="h4" gutterBottom>
                            Emails
                        </Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <SendTestEmail
                            sendEmail={sendTestEmail}
                            sendingEmail={sendingTestEmail}
                        />
                    </Grid>

                    <Grid item xs={12}>
                        {company.partnerId === 1 ? (
                            <Box mt={2} display="flex" flexDirection="column" alignItems="center">
                                <Typography variant="h4" gutterBottom>
                                    Settings
                                </Typography>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={(company?.settings || []).some(s => s.name === 'billing.custom' && s.value === '1')}
                                            onChange={handleCustomBillingToggle}
                                            color="primary"
                                        />
                                    }
                                    label="Custom Billing"
                                    labelPlacement="start"
                                />
                                <Typography variant='caption'>
                                    Note: switching Custom Billing on will overwrite customer chargebee email to: sales@awarego.com and set autoCollection: off, switching it off will NOT return previous values
                                </Typography>
                            </Box>
                        ) : (
                            <Typography variant="h4" gutterBottom>
                                Settings
                            </Typography>
                        )}
                    </Grid>
                    <Grid item xs={12}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={() => navigate(`/companies/settings`)}
                        >
                            View available settings
                        </Button>
                    </Grid>
                    <Grid item xs={12}>
                        <Box mt={4} width="100%">
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                Experimental feature, use with care.
                            </Alert>
                            <Typography variant="h4" gutterBottom>
                                Features
                            </Typography>
                        </Box>
                    </Grid>
                    {FEATURE_SECTIONS.map((section) => (
                        <Grid item xs={12} key={section.id}>
                            <Accordion defaultExpanded={false} sx={{ width: '100%' }}>
                                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                    <Typography variant="h5">{section.label}</Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Grid container spacing={2}>
                                        {section.features
                                            .filter(
                                                (feature) =>
                                                    feature.scope !== 'partner'
                                            )
                                            .map((feature, index) => {
                                            const { checked, source } = computeFeatureState(feature);
                                            const isSaving = savingFeatureIds.has(feature.id);

                                            return (
                                                <Grid
                                                    item
                                                    xs={12}
                                                    md={6}
                                                    key={feature.id}
                                                    sx={{ pl: { md: index % 2 === 0 ? 2 : 0 } }}
                                                >
                                                    <Box display="flex" flexDirection="column" alignItems="flex-start">
                                                        <Box display="flex" alignItems="center" gap={1.5}>
                                                            <Switch
                                                                color="primary"
                                                                checked={Boolean(checked)}
                                                                onChange={(event) =>
                                                                    handleFeatureToggle(
                                                                        feature,
                                                                        event.target.checked
                                                                    )
                                                                }
                                                                disabled={isSaving}
                                                            />
                                                            <Typography variant="subtitle1">
                                                                {feature.label} ({feature.relatedKeys.join(', ')})
                                                            </Typography>
                                                            {source === 'partner' && (
                                                                <Chip
                                                                    label="Partner"
                                                                    size="small"
                                                                    color="info"
                                                                    variant="outlined"
                                                                />
                                                            )}
                                                            {source === 'system' && (
                                                                <Chip
                                                                    label="System"
                                                                    size="small"
                                                                    color="warning"
                                                                    variant="outlined"
                                                                />
                                                            )}
                                                            {isSaving && <CircularProgress size={16} />}
                                                        </Box>
                                                        <Typography
                                                            variant="body2"
                                                            color="textSecondary"
                                                            sx={{ mt: 1 }}
                                                        >
                                                            {feature.description}
                                                        </Typography>
                                                    </Box>
                                                </Grid>
                                            );
                                        })}
                                    </Grid>
                                </AccordionDetails>
                            </Accordion>
                        </Grid>
                    ))}
                    <Grid item xs={12}>
                        <AddSetting
                            saveSetting={addSetting}
                            addingSetting={addingCompanySetting}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormControlLabel
                            control={
                                <Switch
                                    color="primary"
                                    checked={hideFeaturesInTable}
                                    onChange={(event) =>
                                        setHideFeaturesInTable(event.target.checked)
                                    }
                                />
                            }
                            label="Hide Features"
                        />
                    </Grid>
                    {filteredCompanySettings.length > 0 && (
                        <Grid
                            item
                            xs={10}
                            md={6}
                            style={{ maxWidth: '100%', paddingTop: '25px' }}
                        >
                            <SettingsTable
                                settings={filteredCompanySettings}
                                removeSetting={removeSetting}
                                removingSettings={removingCompanySettings}
                                onChangeSettingValue={onChangeSetting}
                                onBlurSettingValue={onBlurSettingValue}
                            />
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <Accordion defaultExpanded={false}>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls="translation-overrides-content"
                                id="translation-overrides-header"
                            >
                                <Typography variant="h4">
                                    Translation Overrides
                                </Typography>
                            </AccordionSummary>
                            <AccordionDetails style={{ display: 'block', padding: 0 }}>
                                <CompanyTranslationsEditor
                                    companyId={companyId}
                                    companySettings={company.settings || []}
                                />
                            </AccordionDetails>
                        </Accordion>
                    </Grid>
                    <Grid item xs={12}>
                        <Box mt={2}>
                            <Typography variant="h5" gutterBottom>
                                PPV package
                            </Typography>
                        </Box>
                    </Grid>
                    {company && !company.ppv_package ? (
                        savingPPV ? (
                            <ThemedSpinner />
                        ) : (
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={createPPV}
                                startIcon={<AddIcon />}
                            >
                                Create a PPV package
                            </Button>
                        )
                    ) : (
                        <Fragment>
                            <Grid item xs={12}>
                                <Grid
                                    container
                                    direction="row"
                                    justify="center"
                                    alignItems="center"
                                    spacing={2}
                                >
                                    <Grid item>
                                        <Typography
                                            display="inline"
                                            color="primary"
                                        >
                                            Created{' '}
                                            {moment(
                                                company.ppv_package.created
                                            ).format('LL')}
                                        </Typography>
                                        <Typography display="inline">
                                            ,{' '}
                                            {moment(
                                                company.ppv_package.created
                                            ).fromNow()}
                                        </Typography>
                                    </Grid>
                                    <Grid item>
                                        <Typography>ApiKey: </Typography>
                                    </Grid>
                                    <Grid item>
                                        <Clipboard
                                            className="copy-to-clipboard-link"
                                            data-clipboard-text={
                                                company.ppv_package.apiKey
                                            }
                                        >
                                            <Button
                                                variant="contained"
                                                startIcon={<FileCopyIcon />}
                                            >
                                                {company.ppv_package.apiKey}
                                            </Button>
                                        </Clipboard>
                                    </Grid>
                                    <Grid item>
                                        {savingPPVApiKey ? (
                                            <ThemedSpinner />
                                        ) : (
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                onClick={newPPVApiKey}
                                                startIcon={<ReplayIcon />}
                                            >
                                                Regenerate
                                            </Button>
                                        )}
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid
                                    container
                                    direction="row"
                                    justify="center"
                                    align="center"
                                    spacing={2}
                                >
                                    <Grid item>
                                        <Typography
                                            variant="body1"
                                            gutterBottom
                                        >
                                            Views Purchased:{' '}
                                            <strong>
                                                {
                                                    company.ppv_package
                                                        .viewsPurchased
                                                }
                                            </strong>
                                        </Typography>
                                    </Grid>
                                    <Grid item>
                                        <Typography
                                            variant="body1"
                                            gutterBottom
                                        >
                                            Views used:{' '}
                                            <strong>
                                                {company.ppv_package.viewsUsed}
                                            </strong>{' '}
                                            (
                                            {isNaN(
                                                (company.ppv_package.viewsUsed /
                                                    company.ppv_package
                                                        .viewsPurchased) *
                                                    100
                                            )
                                                ? 0
                                                : (
                                                      (company.ppv_package
                                                          .viewsUsed /
                                                          company.ppv_package
                                                              .viewsPurchased) *
                                                      100
                                                  ).toFixed(2)}
                                            %)
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item xs={12}>
                                <Grid
                                    container
                                    direction="row"
                                    justify="center"
                                    alignItems="center"
                                    spacing={2}
                                >
                                    <Grid item>
                                        <TextField
                                            type="number"
                                            name="views"
                                            label="Views"
                                            id="views"
                                            onChange={(e) => {
                                                setViews(e.target.value);
                                            }}
                                            value={views}
                                            variant="outlined"
                                        />
                                    </Grid>
                                    <Grid item>
                                        <TextField
                                            type="text"
                                            name="views"
                                            id="notes"
                                            onChange={(e) => {
                                                setNotes(e.target.value);
                                            }}
                                            value={notes}
                                            label="Notes"
                                            variant="outlined"
                                        />
                                    </Grid>
                                    <Grid item>
                                        {savingPPVApiKey ? (
                                            <ThemedSpinner />
                                        ) : (
                                            <Fragment>
                                                <IconButton
                                                    variant="outlined"
                                                    color="primary"
                                                    onClick={addViews}
                                                >
                                                    <AddIcon fontSize="large" />
                                                </IconButton>
                                                <IconButton
                                                    variant="outlined"
                                                    color="secondary"
                                                    onClick={removeViews}
                                                >
                                                    <RemoveIcon fontSize="large" />
                                                </IconButton>
                                            </Fragment>
                                        )}
                                    </Grid>
                                </Grid>
                                <Grid item xs={12}>
                                    <Grid
                                        container
                                        direction="row"
                                        justify="center"
                                        alignItems="center"
                                        spacing={2}
                                    >
                                        <Grid item>
                                            <Link
                                                color="primary"
                                                onClick={() =>
                                                    navigate(
                                                        `${baseUrl}ppv_transactions`
                                                    )
                                                }
                                                variant="body1"
                                            >
                                                Transactions
                                            </Link>
                                        </Grid>
                                        <Grid item>
                                            <Link
                                                color="primary"
                                                onClick={() =>
                                                    navigate(
                                                        `${baseUrl}ppv_log`
                                                    )
                                                }
                                                variant="body1"
                                            >
                                                Views Usage
                                            </Link>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Fragment>
                    )}
                    <Grid item xs={12}>
                        <Box mt={2}>
                            <Typography variant="h5" gutterBottom>
                                Subscription packages
                            </Typography>
                        </Box>
                    </Grid>
                    {company &&
                        company.packages &&
                        company.packages.length > 0 && (
                            <Grid item xs={12}>
                                <PackageTable
                                    company={company}
                                    packages={company.packages}
                                    removePackage={removePackage}
                                    removingPackages={removingPackages}
                                    editPath={baseUrl}
                                    onEditHere={setEditPackageId}
                                />
                            </Grid>
                        )}
                    {company ? (
                        company.partnerId === 1 ? (
                            showAddOrEditForm && (
                                <Grid item xs={12}>
                                    <AddPackage
                                        savePackage={handleAddPackage}
                                        addingPackage={addingPackage}
                                        freemiums={freemiums}
                                        subscriptions={subscriptions}
                                        partnerId={company.partnerId}
                                        isEditMode={editPackageId !== null}
                                        initialPackage={editingPackage}
                                        onCancelEdit={() =>
                                            setEditPackageId(null)
                                        }
                                    />
                                </Grid>
                            )
                        ) : (
                            <Typography>
                                From 6.10.2025 editing companies packages that are not under AwareGO is only allowed through partner portal, use impersonate and manage it in LMS.
                            </Typography>
                        )
                    ) : null}
                    <Grid item xs={12}>
                        <Typography variant="h5">Managers</Typography>
                    </Grid>
                    <Grid item xs={12}>
                        <AddManager
                            addingManager={addingCompanyManager}
                            addManager={addManager}
                        />
                    </Grid>
                    {company.managers && (
                        <Grid item xs={12}>
                            <SimpleTable
                                columns={columns}
                                data={company.managers}
                                needsFilter={company.managers.length > 5}
                            />
                        </Grid>
                    )}

                    {company && aclGroups.length > 0 && (
                        <>
                            <Grid item xs={12}>
                                <Typography variant="h5">
                                    Content Group Access
                                </Typography>
                            </Grid>
                            <Grid item xs={12}>
                                <ContentGroupAccess
                                    aclGroups={aclGroups}
                                    aclRules={aclRulesForPrincipal}
                                    principalUniqueId={company.company_uid}
                                    addToGroup={createAclRule}
                                    removeFromGroup={deleteAclRule}
                                    showLoader={
                                        creatingAclLine ||
                                        removingAclLines.length > 0 ||
                                        loadingAclRulesForPrincipal
                                    }
                                />
                            </Grid>
                        </>
                    )}
                    <Grid item xs={6} md={2}>
                        <Button
                            variant="contained"
                            color="secondary"
                            onClick={() => {
                                deleteCompany();
                            }}
                            startIcon={<DeleteIcon />}
                        >
                            Delete company and all data
                        </Button>
                    </Grid>
                </Fragment>
            )}
            <BillingDialog
                open={showBillingDialog}
                loading={billingDialogLoading}
                pendingPackage={pendingPackage}
                billingForm={localStore.billingForm}
                company={company}
                estimate={superStore.companySubscriptionEstimate}
                cbCustomer={superStore.companyChargebeeCustomer}
                originalCBEmail={originalCBEmail}
                originalCBFirstName={originalCBFirstName}
                originalCBLastName={originalCBLastName}
                onClose={() => setShowBillingDialog(false)}
                onCustomBilling={handleCustomBilling}
                onNormalBilling={handleNormalBilling}
                onBillingContactChange={handleBillingContactChange}
                onRestoreChargebeeData={handleRestoreChargebeeData}
            />
        </Grid>
    );
}

export default observer(CompanyView);
